/** Design Tokens — Level 2: Semantic (CSS var references)
 * Source of truth: styles/tokens-dark.css. No imports from primitives. */

export const bg = {
  app: "var(--cg-bg-app)", base: "var(--cg-bg-base)",
  elevated: "var(--cg-bg-elevated)", sunken: "var(--cg-bg-sunken)",
  surface: "var(--cg-bg-surface)", surfaceHover: "var(--cg-bg-surface-hover)",
  surfaceActive: "var(--cg-bg-surface-active)",
  overlay: "var(--cg-bg-overlay)", overlayHeavy: "var(--cg-bg-overlay-heavy)",
  /** Desktop wall / header seam — matches the TG header chrome so wall ⟷ header ⟷
   *  wall-wide panes read as one surface (no step). */
  wall: "var(--tg-theme-header-bg-color, var(--tg-theme-secondary-bg-color, var(--cg-bg-app)))",
} as const;

export const text = {
  primary: "var(--cg-text-primary)", secondary: "var(--cg-text-secondary)",
  tertiary: "var(--cg-text-tertiary)", muted: "var(--cg-text-muted)",
  disabled: "var(--cg-text-disabled)", onAccent: "var(--cg-text-on-accent)",
  onDark: "#000000",
} as const;

export const border = {
  default: "var(--cg-border-default)", subtle: "var(--cg-border-subtle)",
  muted: "var(--cg-border-muted)", divider: "var(--cg-border-divider)",
} as const;

export const accent = {
  primary: "var(--cg-accent-primary)",
  primaryMuted: "var(--cg-accent-primary-muted)",
  secondary: "var(--cg-accent-secondary)",
  secondaryMuted: "var(--cg-accent-secondary-muted)",
  success: "var(--cg-accent-success)",
  successMuted: "var(--cg-accent-success-muted)",
  warning: "var(--cg-accent-warning)",
  warningMuted: "var(--cg-accent-warning-muted)",
  danger: "var(--cg-accent-danger)",
  dangerMuted: "var(--cg-accent-danger-muted)",
  info: "var(--cg-accent-info)",
  infoMuted: "var(--cg-accent-info-muted)",
  pink: "var(--cg-accent-pink)",
  pinkMuted: "var(--cg-accent-pink-muted)",
  amber: "var(--cg-accent-amber)",
  amberMuted: "var(--cg-accent-amber-muted)",
  cyan: "var(--cg-accent-cyan)",
  cyanMuted: "var(--cg-accent-cyan-muted)",
  violet: "var(--cg-accent-violet)",
  violetMuted: "var(--cg-accent-violet-muted)",
  forced: "var(--cg-accent-forced)",
  forcedMuted: "var(--cg-accent-forced-muted)",
  forcedBorder: "var(--cg-accent-forced-border)",
} as const;

export const space = {
  insetXs: "var(--cg-space-1)", insetSm: "var(--cg-space-2)",
  insetMd: "var(--cg-space-3)", insetLg: "var(--cg-space-4)",
  insetXl: "var(--cg-space-6)",
  stackSm: "var(--cg-space-2)", stackMd: "var(--cg-space-4)",
  stackLg: "var(--cg-space-6)", stackXl: "var(--cg-space-8)",
  pagePadding: "var(--cg-space-5)", sectionGap: "var(--cg-space-6)",
} as const;

export const corners = {
  button: "var(--cg-radius-sm)", card: "var(--cg-radius-md)",
  input: "var(--cg-radius-xs)", sheet: "var(--cg-radius-xl)",
  pill: "var(--cg-radius-full)", icon: "var(--cg-radius-xs)",
} as const;

export const typography = {
  title: { fontSize: "var(--cg-font-2xl)", fontWeight: "var(--cg-weight-bold)" },
  /** Hero-число стат-тайла (DESIGN.md ст.2/7): mono, −0.5, tabular-nums. */
  display: {
    fontSize: "var(--cg-font-2xl)", fontWeight: "var(--cg-weight-bold)",
    fontFamily: "var(--mono)", letterSpacing: "-0.5px",
    fontVariantNumeric: "tabular-nums",
  },
  headline: { fontSize: "var(--cg-font-lg)", fontWeight: "var(--cg-weight-semibold)" },
  body: { fontSize: "var(--cg-font-md)", fontWeight: "var(--cg-weight-normal)" },
  bodyBold: { fontSize: "var(--cg-font-md)", fontWeight: "var(--cg-weight-semibold)" },
  caption: { fontSize: "var(--cg-font-sm)", fontWeight: "var(--cg-weight-normal)" },
  label: { fontSize: "var(--cg-font-xs)", fontWeight: "var(--cg-weight-medium)" },
} as const;

const D = "var(--cg-duration-", E = "var(--cg-ease-";
const _a = (d: string, e: string) => ({ duration: `${D}${d})`, easing: `${E}${e})` });
export const animation = {
  fast: _a("fast", "default"), normal: _a("normal", "default"),
  standard: _a("standard", "default"), slow: _a("slow", "ios"),
  sheet: _a("slow", "ios"), spring: _a("normal", "spring"),
} as const;

const _t = (p: string | string[], d: string, e: string) =>
  Array.isArray(p) ? p.map(x => `${x} ${d} ${e}`).join(", ") : `${p} ${d} ${e}`;
export const transition = {
  fast: (p: string | string[]) => _t(p, `${D}fast)`, `${E}default)`),
  normal: (p: string | string[]) => _t(p, `${D}normal)`, `${E}default)`),
  standard: (p: string | string[]) => _t(p, `${D}standard)`, `${E}default)`),
  slow: (p: string | string[]) => _t(p, `${D}slow)`, `${E}ios)`),
} as const;

export const glass = {
  bg: "var(--cg-glass-bg)", bgPressed: "var(--cg-glass-bg-pressed)",
  bgForced: "var(--cg-glass-bg-forced)", bgSubtle: "var(--cg-glass-bg-subtle)",
  bgFaint: "var(--cg-glass-bg-faint)", bgFaintActive: "var(--cg-glass-bg-faint-active)",
  border: "var(--cg-glass-border)", borderForced: "var(--cg-glass-border-forced)",
  backdrop: "var(--cg-glass-backdrop)",
  iconBg: "var(--cg-glass-icon-bg)", iconBgForced: "var(--cg-glass-icon-bg-forced)",
} as const;

export const shadow = {
  sm: "var(--cg-shadow-sm)", md: "var(--cg-shadow-md)", lg: "var(--cg-shadow-lg)",
  glass: "var(--cg-shadow-glass)", glassForced: "var(--cg-shadow-glass-forced)",
} as const;

export const platform = {
  buttonBg: "var(--tg-theme-button-color, var(--cg-accent-primary))",
  buttonText: "var(--tg-theme-button-text-color, var(--cg-text-on-accent))",
  pageBg: "var(--cg-bg-app)",
} as const;
